import { useState } from "react";
import { formatPageTitle, useDocumentTitle } from "../pageTitle.js";

interface LoginProps {
  requiresNewPassword: boolean;
  onSignIn: (email: string, password: string) => Promise<void>;
  onNewPassword: (newPassword: string) => Promise<void>;
}

/**
 * The two screens are separate components on purpose, so each owns its own `submitting` state.
 *
 * They were previously one component with a shared state bag, which is what left the
 * new-password button stuck disabled: a sign-in that returns the NEW_PASSWORD_REQUIRED
 * challenge *resolves* rather than rejecting (see signIn in auth.ts), so the sign-in form's
 * `submitting` was never cleared -- and because React reuses the same instance when only a prop
 * flips, the second screen mounted already reading `submitting === true`.
 */
export function Login({ requiresNewPassword, onSignIn, onNewPassword }: LoginProps) {
  useDocumentTitle(formatPageTitle("Sign in"));
  return requiresNewPassword ? (
    <NewPasswordForm onSubmit={onNewPassword} />
  ) : (
    <SignInForm onSubmit={onSignIn} />
  );
}

function SignInForm({ onSubmit }: { onSubmit: (email: string, password: string) => Promise<void> }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    onSubmit(email, password)
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : "Sign-in failed.");
      })
      // Always clear, never only on the error path -- a resolved sign-in can still leave this
      // form mounted (the NEW_PASSWORD_REQUIRED challenge is a success, not a failure).
      .finally(() => setSubmitting(false));
  };

  return (
    <div className="app-shell">
      <form className="panel login-panel" onSubmit={handleSubmit}>
        <h1>FeedbackHub Admin</h1>
        <div className="field">
          <label htmlFor="email">Email</label>
          <input
            id="email"
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="password">Password</label>
          <input
            id="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
        {error && <p className="error">{error}</p>}
        <button type="submit" className="btn" disabled={submitting}>
          {submitting ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </div>
  );
}

function NewPasswordForm({ onSubmit }: { onSubmit: (newPassword: string) => Promise<void> }) {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    setError(null);
    setSubmitting(true);
    onSubmit(newPassword)
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : "Could not set new password.");
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <div className="app-shell">
      <form className="panel login-panel" onSubmit={handleSubmit}>
        <h1>Set a new password</h1>
        <p className="muted">
          Your temporary password has expired its single use. Choose a permanent one.
        </p>
        <div className="field">
          <label htmlFor="new-password">New password</label>
          <input
            id="new-password"
            type="password"
            autoComplete="new-password"
            required
            minLength={8}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
          />
          <span className="muted">
            At least 8 characters, with uppercase, lowercase, a number, and a symbol.
          </span>
        </div>
        <div className="field">
          <label htmlFor="confirm-password">Confirm password</label>
          <input
            id="confirm-password"
            type="password"
            autoComplete="new-password"
            required
            minLength={8}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />
        </div>
        {error && <p className="error">{error}</p>}
        <button type="submit" className="btn" disabled={submitting}>
          {submitting ? "Setting password…" : "Set password"}
        </button>
      </form>
    </div>
  );
}
